import { useEffect, useRef, useState } from "react";
import type { ApiError } from "@/types/api";

/** Read-once hook for a single resource — used by detail drawers. */
export function useResource<T>(loader: () => Promise<T>, deps: unknown[]): {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const seq = useRef(0);

  const run = () => {
    const ticket = ++seq.current;
    setLoading(true);
    setError(null);
    setData(null);
    loader()
      .then((d) => {
        if (ticket !== seq.current) return;
        setData(d);
      })
      .catch((e: ApiError) => {
        if (ticket !== seq.current) return;
        setError(e);
      })
      .finally(() => {
        if (ticket !== seq.current) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, reload: run };
}